import { X } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router-dom'

import { Spinner } from '@/components/ui'
import { OfertaBiometriaPrompt } from '@/features/biometria/components/OfertaBiometriaPrompt'
import { useOfertaBiometriaPendente } from '@/features/biometria/hooks/useOfertaBiometriaPendente'
import { PushPermissionPrompt } from '@/features/notificacoes/components/PushPermissionPrompt'
import { usePushSubscriptionSync } from '@/features/notificacoes/hooks/usePushSubscriptionSync'
import { useAccessTokenRefresher } from '@/hooks/useAccessTokenRefresher'
import { useSidebarStore } from '@/stores/sidebarStore'
import { cn } from '@/utils/cn'

import { MobileNav } from './MobileNav'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'

export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const ofertaBiometria = useOfertaBiometriaPendente()

  // Desktop: minimizada (só ícones) por padrão; expande por cima do conteúdo no
  // hover/foco e, "fixada" pelo botão de menu, ocupa a coluna inteira.
  const fixada = useSidebarStore((state) => state.fixada)
  const toggleFixada = useSidebarStore((state) => state.toggleFixada)
  const [sobreposta, setSobreposta] = useState(false)
  const expandida = fixada || sobreposta

  // Rotas marcadas com handle.fullWidth ocupam toda a largura no desktop
  // (sem o max-width centralizado padrão).
  const matches = useMatches()
  const fullWidth = matches.some(
    (match) => (match.handle as { fullWidth?: boolean } | undefined)?.fullWidth,
  )

  // Renova o access token proativamente enquanto a sessão está ativa.
  useAccessTokenRefresher()

  // Religa a inscrição de push deste aparelho ao usuário logado.
  usePushSubscriptionSync()

  // Fecha a gaveta ao trocar de rota (link interno, botão voltar, etc.).
  useEffect(() => setMenuOpen(false), [location.pathname])

  return (
    <div
      className={cn(
        'min-h-dvh lg:grid lg:transition-[grid-template-columns] lg:duration-200 motion-reduce:transition-none',
        fixada ? 'lg:grid-cols-[16rem_1fr]' : 'lg:grid-cols-[4.5rem_1fr]',
      )}
    >
      {/* Sidebar (desktop): coluna reservada + painel que cresce por cima do conteúdo */}
      <aside
        className="relative z-30 hidden lg:sticky lg:top-0 lg:block lg:h-dvh"
        onMouseEnter={() => setSobreposta(true)}
        onMouseLeave={() => setSobreposta(false)}
        onFocus={() => setSobreposta(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setSobreposta(false)
          }
        }}
      >
        <div
          className={cn(
            'absolute inset-y-0 left-0 overflow-hidden transition-[width,box-shadow] duration-200 ease-out motion-reduce:transition-none',
            expandida ? 'w-64' : 'w-[4.5rem]',
            sobreposta && !fixada && 'shadow-2xl shadow-black/30',
          )}
        >
          <Sidebar mini={!expandida} onExpandRequest={() => setSobreposta(true)} />
        </div>
      </aside>

      {/* Gaveta (mobile) — sempre no DOM, animada por transição de CSS */}
      <div
        className={cn(
          'fixed inset-0 z-40 lg:hidden',
          menuOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!menuOpen}
      >
        <div
          onClick={() => setMenuOpen(false)}
          className={cn(
            'absolute inset-0 bg-black/50 transition-opacity duration-300',
            menuOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          className={cn(
            'absolute inset-y-0 left-0 w-72 max-w-[85%] bg-sidebar pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-xl transition-transform duration-300 ease-out',
            menuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Fechar menu"
            className="absolute right-2 top-[calc(0.75rem+env(safe-area-inset-top))] z-10 grid size-9 place-items-center rounded-lg text-sidebar-muted transition-colors hover:bg-white/10"
          >
            <X className="size-5" />
          </button>
          <Sidebar onNavigate={() => setMenuOpen(false)} />
        </div>
      </div>

      <div className="flex min-w-0 flex-col">
        <Topbar
          onOpenMenu={() => setMenuOpen(true)}
          onToggleSidebar={toggleFixada}
          sidebarFixada={fixada}
        />
        <main
          className={cn(
            // pb mobile = altura da barra inferior + área do indicador de gestos (iPhone).
            'w-full flex-1 px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-5 sm:px-6 sm:pt-6 lg:pb-8',
            !fullWidth && 'mx-auto max-w-7xl',
          )}
        >
          <Suspense
            fallback={
              <div className="grid place-items-center py-20">
                <Spinner />
              </div>
            }
          >
            {/* key por rota: re-monta e replay da animação a cada navegação */}
            <div key={location.pathname} className="pc-animate-page">
              <Outlet />
            </div>
          </Suspense>
        </main>
      </div>

      <MobileNav onOpenMenu={() => setMenuOpen(true)} />

      {/* Convites pós-login, um de cada vez: biometria primeiro, depois notificações push. */}
      {ofertaBiometria ? <OfertaBiometriaPrompt /> : <PushPermissionPrompt />}
    </div>
  )
}
